import React, { useState, useEffect, useRef, useCallback } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import Paragraph from '@editorjs/paragraph';
import List from '@editorjs/list';
import Image from '@editorjs/image';
import Quote from '@editorjs/quote';
import Embed from '@editorjs/embed';
import { FiSave, FiX, FiPlus, FiUpload, FiCheck, FiArrowLeft, FiList } from 'react-icons/fi';
import s from './ArticleEditor.module.css';

const STATUS_TABS = [
  { key: 'ALL', label: 'Все' },
  { key: 'DRAFT', label: 'Черновики' },
  { key: 'PUBLISHED', label: 'Опубликованные' },
  { key: 'ARCHIVED', label: 'Архив' }
];

const STATUS_CONFIG = {
  DRAFT: { label: 'Черновик', color: '#6b7280' },
  PUBLISHED: { label: 'Опубликовано', color: '#10b981' },
  ARCHIVED: { label: 'Архив', color: '#ef4444' }
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || { label: status, color: '#6b7280' };
  return (
      <span className={s.statusBadge} style={{ backgroundColor: config.color }}>
      {config.label}
    </span>
  );
};

const LoadingSpinner = () => <div className={s.spinner}></div>;

const ArticlesModal = ({
  isOpen,
  onClose,
  onSelectArticle,
  onCreateNew,
  newsList,
  statusFilter,
  setStatusFilter,
  searchTitleInput,
  setSearchTitleInput,
  loading
}) => {
  if (!isOpen) return null;

  return (
      <div className={s.modalOverlay}>
        <div className={s.modalContent}>
          <div className={s.modalHeader}>
            <h2>Выбор статьи</h2>
            <button className={s.closeButton} onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className={s.modalBody}>
            <div className={s.filterSection}>
              <h3>Фильтр по статусу</h3>
              <div className={s.filterButtons}>
                {STATUS_TABS.map(tab => (
                    <button
                        key={tab.key}
                        className={`${s.filterButton} ${statusFilter === tab.key ? s.active : ''}`}
                        onClick={() => setStatusFilter(tab.key)}
                    >
                      {tab.label}
                    </button>
                ))}
              </div>
            </div>

            <button className={s.newArticleButton} onClick={onCreateNew}>
              <FiPlus /> Новая статья
            </button>

            <div className={s.articlesList}>
              <h3>Список статей ({newsList.length})</h3>

              <input
                  className={s.textInput}
                  placeholder="Поиск по названию (от 3 символов)"
                  value={searchTitleInput}
                  onChange={(e) => setSearchTitleInput(e.target.value)}
                  style={{ marginBottom: 12 }}
              />

              {loading ? (
                  <div className={s.loadingState}>
                    <LoadingSpinner />
                    <p>Загрузка статей...</p>
                  </div>
              ) : newsList.length === 0 ? (
                  <div className={s.emptyState}>
                    {searchTitleInput.length >= 3
                        ? 'По вашему запросу ничего не найдено'
                        : (statusFilter === 'ALL' ? 'Статьи не найдены' : `Нет статей со статусом "${STATUS_TABS.find(t => t.key === statusFilter)?.label}"`)}
                  </div>
              ) : (
                  newsList.map(item => (
                      <div
                          key={item.id}
                          className={s.articleItem}
                          onClick={() => onSelectArticle(item)}
                      >
                        <div className={s.articleTitle}>{item.name}</div>
                        <div className={s.articleDescription}>{item.description}</div>
                        <div className={s.articleMeta}>
                          <StatusBadge status={item.status} />
                          <span className={s.articleDate}>
                      {new Date(item.createdAt || item.updatedAt).toLocaleDateString()}
                    </span>
                        </div>
                      </div>
                  ))
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

const ArticleEditor = ({ onClose, onSave }) => {
  const [editor, setEditor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newsList, setNewsList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedNews, setSelectedNews] = useState(null);
  const [isNewArticle, setIsNewArticle] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(true);
  const [showArticlesModal, setShowArticlesModal] = useState(false);
  const [searchTitleInput, setSearchTitleInput] = useState('');
  const [searchTitle, setSearchTitle] = useState('');
  const [formData, setFormData] = useState({
    source: '',
    name: '',
    description: '',
    isActual: false,
    coverImageUrl: ''
  });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const [error, setError] = useState(null);

  const editorRef = useRef(null);
  const editorInstance = useRef(null);
  const isInitialized = useRef(false);
  const isDestroying = useRef(false);
  const articleContent = useRef(null);

  // Дебаунс поиска по названию
  useEffect(() => {
    const handler = setTimeout(() => setSearchTitle(searchTitleInput.trim()), 300);
    return () => clearTimeout(handler);
  }, [searchTitleInput]);

  // Загрузка списка статей
  const fetchNewsList = useCallback(async (status = null) => {
    try {
      let url = '/api/admin/articles/feed?page=0&size=100';
      if (status && status !== 'ALL') {
        url += `&status=${status}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Ошибка загрузки: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const list = data.content || data || [];
      setNewsList(list);
      setError(null);
    } catch (error) {
      console.error('Ошибка загрузки списка статей:', error);
      setError('Не удалось загрузить список статей');
    }
  }, []);

  // Показываем модальное окно выбора при первом открытии
  useEffect(() => {
    if (selectedNews || isNewArticle) {
      setShowChoiceModal(false);
    }
  }, [selectedNews, isNewArticle]);

  // Уничтожение редактора
  const destroyEditor = useCallback(async () => {
    if (isDestroying.current) return;

    if (editorInstance.current) {
      isDestroying.current = true;
      try {
        if (editorInstance.current.destroy && typeof editorInstance.current.destroy === 'function') {
          await editorInstance.current.destroy();
        }
      } catch (e) {
        console.error('Ошибка при уничтожении редактора:', e);
      }
      editorInstance.current = null;
      setEditor(null);
      isDestroying.current = false;
    }
    isInitialized.current = false;
  }, []);

  // Загрузка контента статьи
  const loadArticleContent = useCallback(async () => {
    if (!selectedNews || isNewArticle) return;

    setLoadingContent(true);
    try {
      const response = await fetch(`/api/articles/${selectedNews.id}/content`);
      if (response.ok) {
        const data = await response.json();

        setFormData({
          isActual: data.isActual || false,
          coverImageUrl: data.coverImageUrl || '',
          name: data.name || '',
          description: data.description || '',
          source: data.source || ''
        });

        // Сохраняем контент для последующего рендеринга
        if (data.contentJson) {
          try {
            articleContent.current = JSON.parse(data.contentJson);
          } catch (e) {
            console.error('Ошибка парсинга контента:', e);
            articleContent.current = null;
          }
        } else {
          articleContent.current = null;
        }
      }
    } catch (e) {
      console.error('Ошибка загрузки контента:', e);
    } finally {
      setLoadingContent(false);
    }
  }, [selectedNews, isNewArticle]);

  // Рендеринг контента в редактор
  const renderContentToEditor = useCallback(async () => {
    if (!editorInstance.current || !articleContent.current) return;

    try {
      await editorInstance.current.isReady;
      // Очищаем редактор перед рендерингом нового контента
      const currentBlocks = await editorInstance.current.save();
      if (currentBlocks.blocks && currentBlocks.blocks.length > 0) {
        editorInstance.current.clear();
      }
      await editorInstance.current.render(articleContent.current);
    } catch (e) {
      console.error('Ошибка рендеринга контента:', e);
    }
  }, []);

  // Инициализация редактора
  const initEditor = useCallback(async () => {
    // Сначала уничтожаем предыдущий редактор
    await destroyEditor();

    if (!editorRef.current) {
      console.warn('Editor ref not available');
      return;
    }

    try {
      const instance = new EditorJS({
        holder: editorRef.current,
        tools: {
          header: {
            class: Header,
            config: {
              placeholder: 'Введите заголовок...',
              levels: [1, 2, 3],
              defaultLevel: 2
            }
          },
          paragraph: { class: Paragraph, inlineToolbar: true },
          list: { class: List, inlineToolbar: true },
          image: {
            class: Image,
            config: {
              uploader: {
                uploadByFile: async (file) => {
                  try {
                    const uuidResponse = await fetch('/api/files/uuid');
                    const uuid = await uuidResponse.text();
                    const extension = file.name.split('.').pop();
                    const fileName = `${uuid}.${extension}`;
                    const fd = new FormData();
                    fd.append('file', file);

                    const uploadResponse = await fetch(
                        `/api/files/newsContent/${selectedNews?.id || 'temp'}/${fileName}`,
                        { method: 'POST', body: fd }
                    );

                    if (uploadResponse.ok) {
                      return {
                        success: 1,
                        file: {
                          url: `/api/files/newsContent/${selectedNews?.id || 'temp'}/${fileName}`
                        }
                      };
                    }

                    throw new Error('Ошибка загрузки изображения');
                  } catch (e) {
                    console.error('Ошибка загрузки изображения:', e);
                    return { success: 0, error: e.message };
                  }
                }
              }
            }
          },
          quote: { class: Quote, inlineToolbar: true },
          embed: {
            class: Embed,
            config: {
              services: { youtube: true, vimeo: true }
            }
          }
        },
        placeholder: 'Начните писать статью...',
        onReady: () => {
          console.log('EditorJS готов к работе');
          isInitialized.current = true;

          // Рендерим контент после готовности редактора
          if (articleContent.current) {
            renderContentToEditor();
          }
        },
        onChange: () => {
          console.log('Контент изменен');
        }
      });

      editorInstance.current = instance;
      setEditor(instance);
    } catch (e) {
      console.error('Ошибка инициализации редактора:', e);
      setError('Не удалось инициализировать редактор');
    }
  }, [destroyEditor, selectedNews, renderContentToEditor]);

  // Загрузка контента при выборе статьи
  useEffect(() => {
    if (selectedNews && !isNewArticle) {
      loadArticleContent();
    } else {
      // Сбрасываем форму для новой статьи
      setFormData({
        source: '',
        name: '',
        description: '',
        isActual: false,
        coverImageUrl: ''
      });
      setCoverFile(null);
      setCoverPreview('');
      articleContent.current = null;
    }
  }, [selectedNews, isNewArticle, loadArticleContent]);

  // Инициализация редактора при выборе статьи или создании новой
  useEffect(() => {
    let mounted = true;

    const initializeEditor = async () => {
      if ((selectedNews || isNewArticle) && !isInitialized.current && mounted) {
        console.log('Инициализация редактора для:', selectedNews?.id || 'new article');
        await initEditor();
      }
    };

    initializeEditor();

    return () => {
      mounted = false;
    };
  }, [initEditor, isNewArticle, selectedNews]);

  // Обработчик изменения обложки
  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setCoverPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Загрузка обложки
  const uploadCover = async () => {
    if (!coverFile || !selectedNews) return '';

    try {
      const extension = coverFile.name.split('.').pop();
      const fileName = `${selectedNews.id}.${extension}`;
      const fd = new FormData();
      fd.append('file', coverFile);

      const resp = await fetch(`/api/files/newsCovers/${fileName}`, {
        method: 'POST',
        body: fd
      });

      if (resp.ok) return `/api/files/newsCovers/${fileName}`;
    } catch (e) {
      console.error('Ошибка загрузки обложки:', e);
    }

    return '';
  };

  // Сохранение статьи
  const handleSave = async (status) => {
    if ((!selectedNews && !isNewArticle) || !editorInstance.current) {
      console.error('Редактор не инициализирован');
      return;
    }

    if (isNewArticle && (!formData.name || !formData.description)) {
      alert('Заполните название и описание');
      return;
    }

    setLoading(true);

    try {
      // Ждем готовности редактора перед сохранением
      if (editorInstance.current.isReady) {
        await editorInstance.current.isReady;
      }
      const content = await editorInstance.current.save();
      const contentJson = JSON.stringify(content);

      if (isNewArticle) {
        // Создание новой статьи
        const createResponse = await fetch('/api/admin/articles/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            source: formData.source,
            name: formData.name,
            description: formData.description,
            isActual: formData.isActual,
            status: status,
            contentJson: contentJson,
            coverImageUrl: ''
          })
        });

        if (createResponse.ok) {
          const created = await createResponse.json();

          if (coverFile) {
            const ext = coverFile.name.split('.').pop();
            const fileName = `${created.id}.${ext}`;
            const fd = new FormData();
            fd.append('file', coverFile);

            await fetch(`/api/files/newsCovers/${fileName}`, {
              method: 'POST',
              body: fd
            });

            await fetch(`/api/articles/${created.id}/content`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: new URLSearchParams({
                isActual: formData.isActual,
                status: status,
                contentJson,
                coverImageUrl: `/api/files/newsCovers/${fileName}`
              })
            });
          }

          const statusMessages = {
            'PUBLISHED': 'Статья опубликована',
            'DRAFT': 'Статья сохранена как черновик',
            'ARCHIVED': 'Статья архивирована'
          };

          alert(statusMessages[status] || 'Статья сохранена');
          onSave && onSave();
          setIsNewArticle(false);
          setSelectedNews(created);
          fetchNewsList(statusFilter);
        } else {
          alert('Ошибка создания статьи');
        }
      } else {
        // Обновление существующей статьи
        let coverImageUrl = formData.coverImageUrl;
        if (coverFile) coverImageUrl = await uploadCover();

        const saveResponse = await fetch(`/api/articles/${selectedNews.id}/content`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            isActual: formData.isActual,
            status: status,
            contentJson,
            coverImageUrl,
            name: formData.name,
            description: formData.description,
            source: formData.source
          })
        });

        if (saveResponse.ok) {
          const statusMessages = {
            'PUBLISHED': 'Статья опубликована',
            'DRAFT': 'Статья сохранена как черновик',
            'ARCHIVED': 'Статья архивирована'
          };

          alert(statusMessages[status] || 'Статья сохранена');
          onSave && onSave();
          fetchNewsList(statusFilter);
        } else {
          alert('Ошибка сохранения');
        }
      }
    } catch (e) {
      console.error('Ошибка сохранения:', e);
      alert('Ошибка сохранения статьи');
    } finally {
      setLoading(false);
    }
  };

  // Создание новой статьи
  const handleNewArticle = async () => {
    // Уничтожаем текущий редактор перед созданием нового
    await destroyEditor();
    setIsNewArticle(true);
    setSelectedNews(null);
    setFormData({
      source: '',
      name: '',
      description: '',
      isActual: false,
      coverImageUrl: ''
    });
    setCoverFile(null);
    setCoverPreview('');
    setShowChoiceModal(false);
    setShowArticlesModal(false);
    articleContent.current = null;
  };

  // Выбор статьи для редактирования
  const handleSelectArticle = async (article) => {
    // Уничтожаем текущий редактор перед загрузкой новой статьи
    await destroyEditor();
    setSelectedNews(article);
    setIsNewArticle(false);
    setShowArticlesModal(false);
    articleContent.current = null;
  };

  const handleOpenArticlesModal = () => {
    setShowArticlesModal(true);
  };

  // Поиск на бэкенде
  useEffect(() => {
    const run = async () => {
      if (searchTitle.length >= 3) {
        try {
          setLoading(true);
          const params = new URLSearchParams({ query: searchTitle, page: '0', size: '100' });
          if (statusFilter && statusFilter !== 'ALL') params.append('status', statusFilter);
          const resp = await fetch(`/api/admin/articles/search?${params.toString()}`);
          if (!resp.ok) throw new Error('Ошибка поиска статей');
          const data = await resp.json();
          setNewsList(data.content || data || []);
        } catch (e) {
          console.error(e);
          setNewsList([]);
        } finally {
          setLoading(false);
        }
      } else {
        fetchNewsList(statusFilter);
      }
    };
    run();
  }, [searchTitle, statusFilter, fetchNewsList]);

  // Очистка при размонтировании компонента
  useEffect(() => {
    return () => {
      destroyEditor();
    };
  }, [destroyEditor]);

  return (
      <div className={s.container}>
        <div className={s.header}>
          <div className={s.headerLeft}>
            <h2>Редактор статей</h2>
            {(selectedNews || isNewArticle) && (
                <button className={s.chooseArticleButton} onClick={handleOpenArticlesModal}>
                  <FiList /> Выбрать статью
                </button>
            )}
          </div>
          <div className={s.actions}>
            <button
                className={s.draftButton}
                onClick={() => handleSave('DRAFT')}
                disabled={(!selectedNews && !isNewArticle) || loading}
            >
              {loading ? <LoadingSpinner /> : <FiSave />}
              {loading ? 'Сохранение...' : 'Черновик'}
            </button>

            <button
                className={s.publishButton}
                onClick={() => handleSave('PUBLISHED')}
                disabled={(!selectedNews && !isNewArticle) || loading}
            >
              {loading ? <LoadingSpinner /> : <FiSave />}
              {loading ? 'Публикация...' : 'Опубликовать'}
            </button>

            {selectedNews && selectedNews.status !== 'ARCHIVED' && (
                <button
                    className={s.archiveButton}
                    onClick={() => handleSave('ARCHIVED')}
                    disabled={loading}
                >
                  <FiSave /> Архивировать
                </button>
            )}

            <button className={s.closeButton} onClick={onClose}>
              <FiX />
            </button>
          </div>
        </div>

        {error && (
            <div className={s.errorMessage}>
              {error}
              <button onClick={() => setError(null)}>×</button>
            </div>
        )}

        {/* Модальное окно выбора действия */}
        {showChoiceModal && !selectedNews && !isNewArticle && (
            <div className={s.modalOverlay}>
              <div className={s.choiceModal}>
                <h3>Что вы хотите сделать?</h3>
                <div className={s.choiceButtons}>
                  <button className={s.publishButton} onClick={handleNewArticle}>
                    Создать новую статью
                  </button>
                  <button
                      className={s.draftButton}
                      onClick={() => {
                        setShowChoiceModal(false);
                        setShowArticlesModal(true);
                      }}
                  >
                    Выбрать существующую
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* Модальное окно выбора статьи */}
        <ArticlesModal
            isOpen={showArticlesModal}
            onClose={() => setShowArticlesModal(false)}
            onSelectArticle={handleSelectArticle}
            onCreateNew={handleNewArticle}
            newsList={newsList}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            searchTitleInput={searchTitleInput}
            setSearchTitleInput={setSearchTitleInput}
            loading={loading}
        />

        {/* Основной контент редактора */}
        {(selectedNews || isNewArticle) && (
            <>
              <div className={s.topBar}>
                <input
                    className={s.textInput}
                    placeholder="Название статьи"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />

                <input
                    className={s.textInput}
                    placeholder="Краткое описание"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />

                <input
                    className={s.textInput}
                    placeholder="Источник (необязательно)"
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                />

                <label className={s.checkboxLabel}>
                  <input
                      type="checkbox"
                      checked={formData.isActual}
                      onChange={(e) => setFormData({ ...formData, isActual: e.target.checked })}
                  />
                  <span>Актуальная</span>
                </label>

                <div className={s.coverUpload}>
                  <label className={s.coverUploadLabel}>
                    <FiUpload />
                    <span>Загрузить обложку</span>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleCoverChange}
                        className={s.fileInput}
                    />
                  </label>

                  {(coverPreview || formData.coverImageUrl) && (
                      <div className={s.coverPreviewContainer}>
                        <img
                            src={coverPreview || formData.coverImageUrl}
                            alt="Превью обложки"
                            className={s.coverPreview}
                        />
                        {coverPreview && <FiCheck className={s.coverUploadSuccess} />}
                      </div>
                  )}
                </div>
              </div>

              <div className={s.editorSection}>
                {loadingContent && (
                    <div className={s.editorLoading}>
                      <LoadingSpinner />
                      <p>Загрузка содержимого...</p>
                    </div>
                )}
                <div
                    ref={editorRef}
                    className={s.editor}
                    style={{ minHeight: '400px' }}
                />
              </div>
            </>
        )}

        {/* Плейсхолдер когда ничего не выбрано */}
        {!selectedNews && !isNewArticle && !showChoiceModal && (
            <div className={s.editorPlaceholder}>
              <FiArrowLeft />
              <h3>Выберите статью для редактирования</h3>
              <p>или создайте новую статью</p>
              <button className={s.publishButton} onClick={() => setShowChoiceModal(true)}>
                Начать работу
              </button>
            </div>
        )}
      </div>
  );
};

export default ArticleEditor;